import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ShieldCheck, UserCog } from "lucide-react";
import { AdminPageShell } from "@/components/shared/admin-page-shell";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RoleTemplatePanel } from "@/features/permissions/components/RoleTemplatePanel";
import { UserPermissionPanel } from "@/features/permissions/components/UserPermissionPanel";
import { useRoles } from "@/features/roles/hooks/useRolesQueries";
import { useUserPermissions } from "@/hooks/useUserPermissions";

type TabKey = "roles" | "user";

export default function UsersPermissionsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialUserId = searchParams.get("userId") ?? undefined;
  const initialRole = searchParams.get("role") ?? "";
  // Super Admin edits every role's template. A Sub-Admin (users:manage-editor-permissions) gets
  // the same two tabs, limited to the Editor role (the backend enforces it).
  const { canManagePermissions } = useUserPermissions();
  const initialTab: TabKey =
    searchParams.get("tab") === "user" || initialUserId ? "user" : "roles";

  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);
  const { data: roles = [], isLoading } = useRoles();

  const editableRoles = roles.filter((role) =>
    canManagePermissions
      ? role.role !== "SUPER_ADMIN"
      : role.role === "EDITOR",
  );
  const [selectedRole, setSelectedRole] = useState(initialRole);

  useEffect(() => {
    const roleFromUrl = searchParams.get("role");
    const tabFromUrl = searchParams.get("tab");
    const userFromUrl = searchParams.get("userId");
    if (roleFromUrl) setSelectedRole(roleFromUrl);
    if (tabFromUrl === "user" || userFromUrl) setActiveTab("user");
    else if (tabFromUrl === "roles" || roleFromUrl) setActiveTab("roles");
  }, [searchParams]);

  const effectiveSelectedRole = useMemo(() => {
    if (selectedRole) {
      if (isLoading || editableRoles.some((r) => r.role === selectedRole)) {
        return selectedRole;
      }
    }
    return editableRoles[0]?.role ?? "";
  }, [selectedRole, editableRoles, isLoading]);

  const handleTabChange = (tab: TabKey) => {
    setActiveTab(tab);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", tab);
      if (tab === "roles") next.delete("userId");
      return next;
    });
  };

  const handleRoleChange = (role: string) => {
    setSelectedRole(role);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", "roles");
      next.set("role", role);
      next.delete("userId");
      return next;
    });
  };

  return (
    <AdminPageShell
      title="الصلاحيات"
      description="تكوين قوالب الأدوار وتخصيص صلاحيات المستخدم فوق قالب دوره"
    >
      <Tabs className="space-y-6">
        <TabsList className="grid h-auto w-full grid-cols-2 gap-1 p-1">
          <TabsTrigger
            type="button"
            data-state={activeTab === "roles" ? "active" : "inactive"}
            className="flex items-center justify-center gap-2 py-2.5"
            onClick={() => handleTabChange("roles")}
          >
            <ShieldCheck className="size-4" />
            قوالب الأدوار
          </TabsTrigger>
          <TabsTrigger
            type="button"
            data-state={activeTab === "user" ? "active" : "inactive"}
            className="flex items-center justify-center gap-2 py-2.5"
            onClick={() => handleTabChange("user")}
          >
            <UserCog className="size-4" />
            تخصيص مستخدم
          </TabsTrigger>
        </TabsList>

        <TabsContent className="mt-0 space-y-6 border-0 p-0">
          {activeTab === "roles" && (
            <div className="space-y-6">
              {canManagePermissions && (
              <Card className="border-primary/20 bg-primary/5">
                <CardContent className="space-y-2 p-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Badge variant="secondary">مدير عام</Badge>
                    <h2 className="text-base font-semibold text-foreground">
                      صلاحيات كاملة
                    </h2>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    يملك المدير العام جميع الصلاحيات على كل الصفحات دائمًا، ولا
                    يمكن تعديل قالبه.
                  </p>
                </CardContent>
              </Card>
              )}

              {isLoading ? (
                <div className="flex justify-center py-12">
                  <Spinner className="size-6" />
                </div>
              ) : editableRoles.length === 0 ? (
                <p className="text-right text-sm text-muted-foreground">
                  لا توجد أدوار قابلة للتعديل.
                </p>
              ) : (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <p className="text-right text-sm font-medium text-foreground">
                      اختر الدور
                    </p>
                    <p className="text-right text-xs text-muted-foreground">
                      عدّل صلاحيات كل دور (المحتوى، المستندات، المستخدمون، النظام).
                    </p>
                    <div className="max-w-md space-y-2">
                      <Select
                        value={effectiveSelectedRole}
                        onValueChange={handleRoleChange}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="اختر الدور" />
                        </SelectTrigger>
                        <SelectContent>
                          {editableRoles.map((role) => (
                            <SelectItem key={role.code} value={role.role}>
                              {role.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {effectiveSelectedRole && (
                    <RoleTemplatePanel editableRole={effectiveSelectedRole} />
                  )}
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent className="mt-0 border-0 p-0">
          {activeTab === "user" && (
            <UserPermissionPanel initialUserId={initialUserId} />
          )}
        </TabsContent>
      </Tabs>
    </AdminPageShell>
  );
}
